/**
 * useKhorosTopic hook helper
 * Interface with Khoros Topics functionality REST API
 *
 * Author: Relevant Bits
 */

import { useContext } from 'react';

import { useQuery } from '@tanstack/react-query';

import { ConfigContext } from '../../../aurr/contexts/ConfigContext';
import LoginContext from '../../../aurr/contexts/LoginContext';
import { cacheTTLs } from '../../../common/constants';
import { buildCacheKey, getAxiosInstance } from '../../axiosCache';
import { TOPIC_QUERY_KEY } from '../constants';
import { KhorosTopicProps } from '../interface/khoros.interface';

export const useKhorosTopic = ({ topicId, baseURL }: KhorosTopicProps) => {
  const { KHOROS_API } = useContext(ConfigContext);
  const { access_token, isUserLoading, userProfile } = useContext(LoginContext);
  const sub = userProfile?.sub;

  // create axios client used in all community function requests
  const axiosConfig = {
    baseURL: baseURL || KHOROS_API,
    headers: {
      ...(access_token && { authorization: `Bearer ${access_token}` }),
      'Cache-Control': 'no-cache, max-age=0',
    },
    id: buildCacheKey([TOPIC_QUERY_KEY, topicId, sub]),
    cache: {
      ttl: cacheTTLs.profile,
    },
  };

  const api = getAxiosInstance();
  const endpointPathname = access_token ? 'mytopic' : 'topic';

  return useQuery({
    queryKey: [TOPIC_QUERY_KEY, { topicId, sub }],
    queryFn: async () => {
      try {
        const response = await api.get(
          `/services/v2/community/khoros/${endpointPathname}?id=${topicId}`,
          axiosConfig
        );
        return response?.data || undefined;
      } catch (error) {
        return undefined;
      }
    },
    enabled: !isUserLoading,
  });
};
